import React, { useEffect, useRef, useState } from 'react';
import { User, Interview } from '../types';
import { api } from '../services/api';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Monitor, MessageSquare, Clock, ShieldCheck, UserCheck } from 'lucide-react';

interface WebRTCInterviewRoomProps {
  interview: Interview;
  user: User;
  onClose: () => void;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export const WebRTCInterviewRoom: React.FC<WebRTCInterviewRoomProps> = ({
  interview,
  user,
  onClose,
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [connectionState, setConnectionState] = useState<string>('Initializing');
  const [callDuration, setCallDuration] = useState(0);
  const [interviewNotes, setInterviewNotes] = useState(interview.notes || '');

  const senderId = `${user.email}_${Math.random().toString(36).substring(2, 6)}`;

  // Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format seconds
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Setup WebRTC and Media Devices
  useEffect(() => {
    let isSubscribed = true;
    let pollInterval: any = null;

    async function initMediaAndWebRTC() {
      try {
        setConnectionState('Accessing Camera & Mic...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        if (!isSubscribed) return;
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        setConnectionState('Connecting to Interview Room...');

        // Initialize PeerConnection
        const pc = new RTCPeerConnection(ICE_SERVERS);
        peerRef.current = pc;

        // Add local tracks to peer connection
        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
        });

        // Remote track handling
        const remoteMediaStream = new MediaStream();
        setRemoteStream(remoteMediaStream);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteMediaStream;
        }

        pc.ontrack = (event) => {
          event.streams[0].getTracks().forEach((t) => {
            remoteMediaStream.addTrack(t);
          });
          setConnectionState('Connected');
        };

        // ICE candidate handler
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            api.sendSignal(interview.room_id, senderId, {
              type: 'candidate',
              candidate: event.candidate,
            }).catch(console.error);
          }
        };

        pc.onconnectionstatechange = () => {
          setConnectionState(pc.connectionState);
        };

        // If user is interviewer, create Offer
        const isInterviewer = user.email.toLowerCase() === interview.interviewer_email.toLowerCase() || user.role === 'hiring_team';
        if (isInterviewer) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await api.sendSignal(interview.room_id, senderId, {
            type: 'offer',
            sdp: offer,
          });
        }

        // Poll signaling broker for remote offers/answers/candidates
        pollInterval = setInterval(async () => {
          try {
            const res = await api.pollSignals(interview.room_id, senderId);
            if (!res.signals) return;

            for (const sig of res.signals) {
              const { payload } = sig;
              if (!pc) continue;

              if (payload.type === 'offer' && !isInterviewer && pc.signalingState === 'stable') {
                await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                await api.sendSignal(interview.room_id, senderId, {
                  type: 'answer',
                  sdp: answer,
                });
              } else if (payload.type === 'answer' && isInterviewer && pc.signalingState === 'have-local-offer') {
                await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
              } else if (payload.type === 'candidate') {
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
                } catch (e) {
                  // Candidate race condition ignorable
                }
              }
            }
          } catch (e) {
            console.error('Signal poll error:', e);
          }
        }, 1500);

      } catch (err: any) {
        console.error('WebRTC error:', err);
        setConnectionState('Camera/Mic permission denied or unavailable');
      }
    }

    initMediaAndWebRTC();

    return () => {
      isSubscribed = false;
      if (pollInterval) clearInterval(pollInterval);
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (peerRef.current) {
        peerRef.current.close();
      }
    };
  }, [interview.room_id]);

  // Toggle Mute Audio
  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
      }
    }
  };

  // Toggle Mute Video
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoMuted(!videoTrack.enabled);
      }
    }
  };

  // Toggle Screen Share
  const toggleScreenShare = async () => {
    if (!peerRef.current) return;

    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];

        const sender = peerRef.current.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(screenTrack);
        }

        screenTrack.onended = () => {
          if (localStream && sender) {
            sender.replaceTrack(localStream.getVideoTracks()[0]);
          }
          setIsScreenSharing(false);
        };

        setIsScreenSharing(true);
      } catch (e) {
        console.error('Screen share error:', e);
      }
    } else {
      const sender = peerRef.current.getSenders().find((s) => s.track?.kind === 'video');
      if (sender && localStream) {
        sender.replaceTrack(localStream.getVideoTracks()[0]);
      }
      setIsScreenSharing(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ background: '#04070e', padding: '16px' }}>
      <div
        style={{
          width: '100%',
          maxWidth: '1240px',
          height: '92vh',
          background: 'var(--color-navy-dark)',
          border: '1px solid var(--color-beige-border)',
          borderRadius: 'var(--radius-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
        }}
      >
        {/* Call Header */}
        <div
          style={{
            padding: '16px 24px',
            background: 'var(--color-navy-modal)',
            borderBottom: '1px solid var(--color-beige-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-aqua)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              SansCraft Live Video Interview Room
            </span>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '2px' }}>
              {interview.job_title} Candidate Interview
            </h3>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              <Clock size={16} color="var(--color-aqua)" />
              {formatTime(callDuration)}
            </div>

            <span
              style={{
                fontSize: '0.8rem',
                fontWeight: 700,
                padding: '4px 12px',
                borderRadius: '20px',
                background: connectionState === 'Connected' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                color: connectionState === 'Connected' ? 'var(--accent-emerald)' : 'var(--accent-amber)',
                border: `1px solid ${connectionState === 'Connected' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
              }}
            >
              {connectionState}
            </span>

            <button className="btn-secondary" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={onClose}>
              Exit Room
            </button>
          </div>
        </div>

        {/* Video Grid & Notes Panel */}
        <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
          {/* Main Video View */}
          <div style={{ flex: 1, background: '#02050b', display: 'flex', flexDirection: 'column', padding: '16px', position: 'relative' }}>
            
            {/* Remote Peer Video Window */}
            <div
              style={{
                flex: 1,
                background: '#070c18',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-beige-border)',
              }}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />

              {connectionState !== 'Connected' && (
                <div style={{ position: 'absolute', textAlign: 'center', padding: '20px' }}>
                  <UserCheck size={48} color="var(--color-aqua)" style={{ marginBottom: '12px' }} />
                  <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>Waiting for peer to connect...</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Share room ID: <strong style={{ color: 'var(--color-aqua)', fontFamily: 'var(--font-mono)' }}>{interview.room_id}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Local Video Thumbnail Overlay */}
            <div
              style={{
                position: 'absolute',
                bottom: '32px',
                right: '32px',
                width: '220px',
                height: '140px',
                background: '#0d172c',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                border: '2px solid var(--color-aqua)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
                zIndex: 10,
              }}
            >
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <span
                style={{
                  position: 'absolute',
                  bottom: '6px',
                  left: '8px',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#fff',
                  background: 'rgba(0,0,0,0.6)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}
              >
                You ({user.name})
              </span>
            </div>
          </div>

          {/* Notes & Interview Details Drawer */}
          <div
            style={{
              width: '320px',
              background: 'var(--color-navy-modal)',
              borderLeft: '1px solid var(--color-beige-border)',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--color-aqua)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MessageSquare size={16} /> Interview Notes & Agenda
            </h4>

            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.5 }}>
              <div>Candidate: <strong style={{ color: 'var(--text-main)' }}>{interview.candidate_name}</strong></div>
              <div>Email: <strong style={{ color: 'var(--text-main)' }}>{interview.candidate_email}</strong></div>
            </div>

            <textarea
              className="form-textarea"
              style={{ flex: 1, resize: 'none', fontSize: '0.85rem', lineHeight: 1.6 }}
              placeholder="Record technical evaluation notes, code discussion feedback, or questions during the interview..."
              value={interviewNotes}
              onChange={(e) => setInterviewNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Action Controls Bar */}
        <div
          style={{
            padding: '16px 24px',
            background: 'var(--color-navy-modal)',
            borderTop: '1px solid var(--color-beige-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <button
            className={`btn-secondary ${isAudioMuted ? 'active' : ''}`}
            style={{
              padding: '12px 18px',
              borderRadius: '50px',
              borderColor: isAudioMuted ? 'var(--accent-rose)' : 'var(--color-beige-border)',
              color: isAudioMuted ? 'var(--accent-rose)' : 'var(--text-main)',
            }}
            onClick={toggleAudio}
            title={isAudioMuted ? 'Unmute Microphone' : 'Mute Microphone'}
          >
            {isAudioMuted ? <MicOff size={20} /> : <Mic size={20} />}
          </button>

          <button
            className={`btn-secondary ${isVideoMuted ? 'active' : ''}`}
            style={{
              padding: '12px 18px',
              borderRadius: '50px',
              borderColor: isVideoMuted ? 'var(--accent-rose)' : 'var(--color-beige-border)',
              color: isVideoMuted ? 'var(--accent-rose)' : 'var(--text-main)',
            }}
            onClick={toggleVideo}
            title={isVideoMuted ? 'Turn Camera On' : 'Turn Camera Off'}
          >
            {isVideoMuted ? <VideoOff size={20} /> : <Video size={20} />}
          </button>

          <button
            className={`btn-secondary ${isScreenSharing ? 'active' : ''}`}
            style={{
              padding: '12px 18px',
              borderRadius: '50px',
              borderColor: isScreenSharing ? 'var(--color-aqua)' : 'var(--color-beige-border)',
              color: isScreenSharing ? 'var(--color-aqua)' : 'var(--text-main)',
            }}
            onClick={toggleScreenShare}
            title="Share Screen"
          >
            <Monitor size={20} />
          </button>

          <button
            style={{
              background: 'var(--accent-rose)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              padding: '12px 28px',
              borderRadius: '50px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 20px rgba(244, 63, 94, 0.4)',
            }}
            onClick={onClose}
          >
            <PhoneOff size={18} /> End Interview
          </button>
        </div>
      </div>
    </div>
  );
};
